import * as React from 'react'
import { Link } from 'react-router-dom'
import { docsHubDocumentHref, plantPlcNodeHref } from '@atbash-labs/plantview-links'
import {
  BookOpen,
  Cpu,
  ExternalLink,
  History,
  LineChart,
  LoaderCircle,
  MessageSquare,
  Shapes,
  Unplug,
  Wrench,
  Zap
} from 'lucide-react'
import {
  useGetPlantPlcNodeDetailQuery,
  useListOccurrenceDebugItemsQuery,
  useResolveDocQuery,
  useListOccurrenceInvestigationsQuery,
  useListProblemTasksQuery,
  type ProblemDebugItem,
  type ProblemDebugItemKind,
  type ProblemOccurrence,
  type ProblemTask,
  type WorkScope
} from '@axilon-apps/domains-ontology-ui-state'
import { Button } from '../ui/button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet'
import { SourceDocViewer, useSourceDocViewer } from '../SourceDocViewer'
import { StatusPill } from '../primitives/StatusPill'
import { cn } from '../../lib/utils'
import { formatCompactLocalTime } from '../../lib/time'
import { useChatHandoff } from '../../lib/use-chat-handoff'
import { occurrenceContext, visibleProblemTasks } from './work-model'
import { investigationStages, trendPath, type InvestigationStages } from './investigation-model'
import {
  evidenceColumns,
  inlineReading,
  plcCodeLines,
  routineRungs,
  rungWindow,
  startHereItems
} from './evidence-model'

const KIND_ICON: Record<ProblemDebugItemKind, typeof LineChart> = {
  signal: LineChart,
  connection: Unplug,
  plc_logic: Cpu,
  document: BookOpen,
  drawing: Zap,
  equipment: Wrench,
  past_fix: History,
  other: Shapes
}

const KIND_LABEL: Record<ProblemDebugItemKind, string> = {
  signal: 'Signal',
  connection: 'Connection',
  plc_logic: 'PLC logic',
  document: 'Document',
  drawing: 'Drawing',
  equipment: 'Equipment',
  past_fix: 'Past fix',
  other: 'Evidence'
}

const arrive = 'animate-in fade-in slide-in-from-bottom-1 duration-300 motion-reduce:animate-none'

/** Stage progress for one occurrence, from the same queries its page reads. */
export function useInvestigationStages(
  scope: WorkScope,
  occurrence: ProblemOccurrence,
  enabled = true
) {
  const record = { ...scope, id: occurrence.id }
  const runs = useListOccurrenceInvestigationsQuery(record, { skip: !enabled })
  const items = useListOccurrenceDebugItemsQuery(record, { skip: !enabled })
  const tasks = useListProblemTasksQuery(record, { skip: !enabled })
  const taskCount = visibleProblemTasks(tasks.currentData?.tasks ?? [], 'open').length
  return {
    items,
    stages: investigationStages({
      runs: runs.isError ? undefined : runs.currentData,
      likelyCause: occurrence.likely_cause,
      debugItems: items.currentData?.length ?? 0,
      debugItemsFailed: items.isError,
      tasks: taskCount
    })
  }
}

/** Shows what a live run is working on; nothing once it is done. */
export function InvestigationProgress({ stages }: { stages: InvestigationStages }) {
  if (!stages.progress) return null
  return (
    <StatusPill status="info" withDot={false} className="whitespace-nowrap">
      <LoaderCircle size={11} className="animate-spin motion-reduce:animate-none" aria-hidden />
      {stages.progress}
    </StatusPill>
  )
}

function Sparkline({ values, className }: { values: readonly number[]; className?: string }) {
  return (
    <svg
      viewBox="0 0 100 28"
      preserveAspectRatio="none"
      className={cn('overflow-visible', className)}
      aria-hidden
    >
      <path
        d={trendPath(values)}
        className="fill-none stroke-brand"
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

/** One line per item: what it is, where it is from, and for a signal its trend. Opens the details panel. */
function EvidenceLine({ item, onOpen }: { item: ProblemDebugItem; onOpen: () => void }) {
  const reading = inlineReading(item)
  return (
    <li className={cn('min-w-0', arrive)}>
      <button
        type="button"
        onClick={onOpen}
        className="-mx-1.5 grid w-[calc(100%+0.75rem)] grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 rounded px-1.5 py-1.5 text-left outline-none transition-colors hover:bg-bg-sunk/60 focus-visible:ring-2 focus-visible:ring-brand"
      >
        <span className="min-w-0">
          <span className="block break-words text-13 font-medium leading-5 text-brand">
            {item.title}
          </span>
          <span className="block truncate font-mono text-11 text-ink-3">{item.source}</span>
        </span>
        {(item.trend?.length ?? 0) > 1 || reading ? (
          <span className="flex items-center gap-2">
            {item.trend && item.trend.length > 1 && (
              <Sparkline values={item.trend} className="h-5 w-16" />
            )}
            {reading && (
              <span className="whitespace-nowrap font-mono text-12 tabular-nums text-ink">
                {reading}
              </span>
            )}
          </span>
        ) : null}
      </button>
    </li>
  )
}

/** Up to three numbered places to start, each opening its evidence. */
export function StartHere({
  items,
  onOpen
}: {
  items: readonly ProblemDebugItem[]
  onOpen: (item: ProblemDebugItem) => void
}) {
  const steps = startHereItems(items)
  if (!steps.length) return null
  return (
    <section aria-label="Start here" className="flex flex-col gap-2">
      <h2 className="m-0 text-12 font-semibold uppercase tracking-wide text-ink-2">Start here</h2>
      <ol className="m-0 flex list-none flex-col gap-2 p-0">
        {steps.map((item, index) => (
          <li key={item.id} className={arrive}>
            <button
              type="button"
              onClick={() => onOpen(item)}
              className="grid w-full grid-cols-[1.5rem_minmax(0,1fr)] items-start gap-3 rounded-lg border border-line bg-bg-sunk/40 px-3 py-2.5 text-left outline-none transition-colors hover:border-line-2 focus-visible:ring-2 focus-visible:ring-brand"
            >
              <span className="flex size-6 items-center justify-center rounded-full bg-brand text-12 font-semibold text-white">
                {index + 1}
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-1.5 text-11 text-ink-3">
                  {React.createElement(KIND_ICON[item.kind], { size: 12, 'aria-hidden': true })}
                  {KIND_LABEL[item.kind]}
                </span>
                <span className="block break-words text-14 font-medium leading-5 text-ink">
                  {item.title}
                </span>
                <span className="block break-words text-12 text-ink-3">{item.source}</span>
              </span>
            </button>
          </li>
        ))}
      </ol>
    </section>
  )
}

/** All evidence in three flat columns, one line per item. */
export function EvidenceColumns({
  items,
  collecting,
  onOpen
}: {
  items: readonly ProblemDebugItem[]
  collecting: boolean
  onOpen: (item: ProblemDebugItem) => void
}) {
  const columns = evidenceColumns(items)
  if (!items.length && !collecting) return null
  return (
    <section aria-label="Evidence" className="flex flex-col gap-2">
      {items.length > 0 && (
        <div className="grid grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-[1.3fr_1fr_1fr]">
          {columns.map((sections, index) => (
            <div key={index} className="flex min-w-0 flex-col gap-4">
              {sections.map((section) => (
                <section key={section.label} aria-label={section.label} className="min-w-0">
                  <h3 className="m-0 mb-1 flex items-center gap-2 text-12 font-semibold uppercase tracking-wide text-ink-2">
                    {section.label}
                    <span className="font-normal tabular-nums text-ink-3">
                      {section.items.length}
                    </span>
                  </h3>
                  <ul className="m-0 flex list-none flex-col divide-y divide-line/70 p-0">
                    {section.items.map((item) => (
                      <EvidenceLine key={item.id} item={item} onOpen={() => onOpen(item)} />
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          ))}
        </div>
      )}
      {collecting && (
        <p className="m-0 flex items-center gap-2 text-13 text-ink-3">
          <LoaderCircle size={13} className="animate-spin motion-reduce:animate-none" aria-hidden />
          Checking signals, PLC logic, manuals, drawings and past fixes…
        </p>
      )}
    </section>
  )
}

/** Everything about one item, in a side panel, with Ask in chat about exactly that item. */
export function EvidencePanel({
  item,
  occurrence,
  onClose
}: {
  item: ProblemDebugItem | null
  occurrence: ProblemOccurrence
  onClose: () => void
}) {
  const { askInChat } = useChatHandoff()
  const { openSource, viewerProps } = useSourceDocViewer()
  return (
    <>
      <Sheet open={item !== null} onOpenChange={(open) => !open && onClose()}>
        <SheetContent side="right" className="w-full gap-0 sm:max-w-xl">
          {item && (
            <>
              <SheetHeader className="border-b border-line">
                <span className="flex items-center gap-1.5 text-11 font-medium uppercase tracking-wide text-ink-3">
                  {React.createElement(KIND_ICON[item.kind], { size: 12, 'aria-hidden': true })}
                  {KIND_LABEL[item.kind]}
                </span>
                <SheetTitle className="break-words pr-6 text-15">{item.title}</SheetTitle>
                <SheetDescription className="break-words font-mono text-12">
                  {item.source}
                </SheetDescription>
              </SheetHeader>
              <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4">
                {item.trend && item.trend.length > 1 && (
                  <Sparkline values={item.trend} className="h-24 w-full" />
                )}
                {item.facts && item.facts.length > 0 && (
                  <dl className="m-0 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1 text-13">
                    {item.facts.map((fact, index) => (
                      <React.Fragment key={`${fact.label}:${index}`}>
                        <dt className="text-12 leading-5 text-ink-3">{fact.label}</dt>
                        <dd className="m-0 min-w-0 break-words text-ink">{fact.value}</dd>
                      </React.Fragment>
                    ))}
                  </dl>
                )}
                {item.kind === 'plc_logic' && item.target?.node_id ? (
                  <RungInContext item={item} routineId={item.target.node_id} />
                ) : (
                  item.code && <CodeLines code={item.code} />
                )}
                {item.kind === 'document' && (
                  <OpenInDocument
                    item={item}
                    onOpen={(ref) => {
                      onClose()
                      openSource(ref)
                    }}
                  />
                )}
                {item.body && (
                  <p
                    className={cn(
                      'm-0 whitespace-pre-wrap break-words text-13 leading-relaxed text-ink-2',
                      item.kind === 'document' && 'border-l-2 border-line-2 pl-2.5'
                    )}
                  >
                    {item.body}
                  </p>
                )}
                <time dateTime={item.created_at} className="text-12 text-ink-3">
                  Found {formatCompactLocalTime(item.created_at)}
                </time>
              </div>
              <div className="border-t border-line p-4">
                <Button
                  size="sm"
                  variant="outline"
                  className="text-brand"
                  onClick={() =>
                    askInChat({
                      title: item.title,
                      context: {
                        ...occurrenceContext(occurrence),
                        runId: item.producing_run_id,
                        debugItem: {
                          id: item.id,
                          kind: item.kind,
                          title: item.title,
                          source: item.source,
                          ...(item.target ? { target: { ...item.target } } : {})
                        }
                      }
                    })
                  }
                >
                  <MessageSquare size={14} />
                  Ask in chat
                </Button>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
      <SourceDocViewer {...viewerProps} />
    </>
  )
}

/** Rung or routine text with line numbers; a ladder rung reads one instruction per line. */
function CodeLines({ code }: { code: string }) {
  return (
    <ol className="m-0 list-none overflow-x-auto rounded bg-bg-sunk py-2 font-mono text-12 leading-relaxed text-ink-2">
      {plcCodeLines(code).map((line, index) => {
        const call = line.marker ? null : /^([A-Za-z_][\w.]*)\((.*)\)$/.exec(line.text)
        return (
          <li key={index} className="grid grid-cols-[2.25rem_minmax(0,1fr)]">
            <span className="select-none pr-2 text-right tabular-nums text-ink-4">{index + 1}</span>
            <span
              className={cn(
                'whitespace-pre-wrap break-all pr-2.5',
                line.marker && 'italic text-ink-3'
              )}
              style={{ paddingLeft: `${line.depth * 1.25}rem` }}
            >
              {call ? (
                <>
                  <span className="font-semibold text-brand">{call[1]}</span>({call[2]})
                </>
              ) : (
                line.text
              )}
            </span>
          </li>
        )
      })}
    </ol>
  )
}

/**
 * The cited rung among its neighbours in the routine, highlighted and
 * scrolled into view; the whole routine on request. Falls back to the
 * item's own code while the routine loads or when it cannot be read.
 */
function RungInContext({ item, routineId }: { item: ProblemDebugItem; routineId: string }) {
  const detail = useGetPlantPlcNodeDetailQuery(routineId)
  const [all, setAll] = React.useState(false)
  const cited = React.useRef<HTMLLIElement>(null)
  const routine = detail.currentData?.kind === 'Routine' ? detail.currentData.routine : null
  const rungs = React.useMemo(() => routineRungs(routine?.rungs), [routine])
  const nearby = rungWindow(rungs, item.target?.rung)
  const shown = all ? rungs : (nearby?.rungs ?? [])
  React.useEffect(() => {
    cited.current?.scrollIntoView?.({ block: 'center' })
  }, [shown.length])
  if (!nearby) return item.code ? <CodeLines code={item.code} /> : null
  return (
    <section aria-label="Rung in its routine" className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2 text-12 text-ink-3">
        <span>
          {routine?.name} · {rungs.length} rungs
        </span>
        <Link
          to={plantPlcNodeHref(routineId)}
          className="font-medium text-brand no-underline hover:underline"
        >
          Open in PLC view
        </Link>
      </div>
      {!all && nearby.before > 0 && (
        <Button size="sm" variant="ghost" className="self-start" onClick={() => setAll(true)}>
          Show {nearby.before} earlier {nearby.before === 1 ? 'rung' : 'rungs'}
        </Button>
      )}
      <ol className="m-0 flex list-none flex-col gap-1.5 p-0">
        {shown.map((rung) => {
          const isCited = rung.number === item.target?.rung
          return (
            <li
              key={rung.number}
              ref={isCited ? cited : undefined}
              aria-current={isCited ? 'true' : undefined}
              className={cn(
                'rounded border px-2 py-1.5',
                isCited ? 'border-brand bg-brand/5' : 'border-line/70 opacity-80'
              )}
            >
              <p className="m-0 mb-1 text-11 font-semibold uppercase tracking-wide text-ink-3">
                Rung {rung.number}
                {rung.comment && (
                  <span className="ml-2 font-normal normal-case tracking-normal">
                    {rung.comment}
                  </span>
                )}
              </p>
              <CodeLines code={rung.logic} />
            </li>
          )
        })}
      </ol>
      {!all && nearby.after > 0 && (
        <Button size="sm" variant="ghost" className="self-start" onClick={() => setAll(true)}>
          Show {nearby.after} later {nearby.after === 1 ? 'rung' : 'rungs'}
        </Button>
      )}
    </section>
  )
}

/**
 * Opens the document the excerpt quotes at its page: an ingest-corpus file
 * in the source viewer beside the excerpt, a library document on its page.
 */
function OpenInDocument({
  item,
  onOpen
}: {
  item: ProblemDebugItem
  onOpen: (ref: {
    jobId: string
    sourceFile: string
    sourcePage?: number
    sourceExcerpt?: string
  }) => void
}) {
  const path = item.target?.doc_path
  const jobId = item.target?.job_id
  const page = Number.parseInt(item.target?.page ?? '', 10)
  const sourcePage = Number.isInteger(page) && page > 0 ? page : undefined
  // Without its ingest job, look the file up in the library: by its own path,
  // or under Ingested Documents, where unstructured ingest copies every file.
  const lookUp = Boolean(path && !jobId && !item.target?.doc_id)
  const direct = useResolveDocQuery(path ?? '', { skip: !lookUp })
  const ingested = useResolveDocQuery(`ingested/${path ?? ''}`, {
    skip: !lookUp || !direct.isError
  })
  const label = sourcePage ? `Open page ${sourcePage} in the document` : 'Open the document'
  if (!path) return null
  if (jobId) {
    return (
      <Button
        size="sm"
        variant="outline"
        className="self-start"
        onClick={() => onOpen({ jobId, sourceFile: path, sourcePage, sourceExcerpt: item.body })}
      >
        <BookOpen size={14} />
        {label}
      </Button>
    )
  }
  const libraryPath = item.target?.doc_id
    ? path
    : (direct.currentData?.path ?? ingested.currentData?.path)
  if (!libraryPath) return null
  return (
    <Button size="sm" variant="outline" className="self-start" asChild>
      <Link to={docsHubDocumentHref(libraryPath, sourcePage)}>
        <ExternalLink size={14} />
        {label}
      </Link>
    </Button>
  )
}

/** A draft's tasks are not published yet, so they read only. */
export function DraftTasks({ tasks }: { tasks: readonly ProblemTask[] }) {
  if (!tasks.length) return <p className="m-0 px-3 py-2.5 text-13 text-ink-3">No tasks yet.</p>
  return (
    <ol className="m-0 flex list-none flex-col divide-y divide-line/70 p-0">
      {tasks.map((task) => (
        <li key={task.id} className={cn('px-3 py-2.5', arrive)}>
          <p className="m-0 break-words text-13 font-medium leading-5 text-ink">{task.title}</p>
          {task.instructions && (
            <p className="m-0 mt-1 whitespace-pre-wrap break-words text-13 leading-relaxed text-ink-2">
              {task.instructions}
            </p>
          )}
        </li>
      ))}
    </ol>
  )
}
